import { useEffect, useState } from "react";
import { Auth } from "aws-amplify";
import styled, { keyframes } from 'styled-components';
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import DefaultLayout from "../layouts/DefaultLayout";
import { useTranslation } from "react-i18next";
import { emailIsValid, fetchQuery } from "../functions/misc";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import Message from "../components/Message";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { clientConfig } from "../awsClientConfig";
import {Buffer} from "buffer";
import { Spinner } from "react-bootstrap";
import { AuthenticatedToLegacy, RedirectToLegacy } from "../Common/LegacyLogin";
import PortalClientModal from './PortalClientModal';
import { CustomerRoleName, useProfileStore, useRoleStore, EnrollmentSource } from "../store/ProfileStore";
import { AnimatePresence, motion } from 'framer-motion';
import useMotionStore from "../store/MotionStore";
import useFeatureStore, { usePortalStore } from "../store/FeatureStore";
import { Snackbar, Alert } from "@mui/material";
import { paths } from "../paths";
import useEnrollmentStore, { SubscriptionStatus } from "../store/EnrollmentStore";
import useAuthStore from "../store/AuthStore";
import useLeadStore from "../store/LeadStore";
import { GetProfile } from "../queries/getProfile";
import { GetPartner } from "../queries/Partners";
import { GetProfileRoles } from "../queries/getProfileRoles";
import { GetProtectionPlanSubscription } from "../queries/GetProtectionPlanSubscription";
import { GetLeadsByPartner } from "../queries/Leads";
import { LeadSummaryConnection } from "../API";

interface FormError {
  email: string;
  password: string;
}

interface AnimatedDotProps {
  delay: string;
}

const LoginPage = () => {
  const navigate = useNavigate();
  const { reset } = useParams();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation("common");
  const [error, setError] = useState<FormError>({ email: "", password: "" });
  const [submited, setSubmited] = useState(false);
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState("");
  const [pwdIcon, setPwdIcon] = useState(faEyeSlash);
  const [pwdType, setPwdType] = useState("password");
  // const [rememberMe, setRememberMe] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [showPortalModal, setShowPortalModal] = useState<boolean>(false);
  const [customTitle, setCustomTitle] = useState('');
  const [customBody, setCustomBody] = useState('');
  const [feedbackPrompt, setFeedbackPrompt] = useState(false);
  const [clientPortalSignup, setClientPortalSignup] = useState<boolean>(false);
  const [clientPortalSignIn, setClientPortalSignIn] = useState<boolean>(false);
  const [didPasswordReset, setDidPasswordReset] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [limitExceed, setLimitExceeded] = useState(false);
  const { primaryProfile, setPrimaryProfile, setSpouseProfile, setPartnerProfile, setEConsent } = useProfileStore();
  const { setMotionLayout } = useMotionStore();
  const { featureFlags, setFeatureFlags } = useFeatureStore();
  const { setRedirectedToLegacy, clearPortalStore } = usePortalStore();
  const { customerRole, setCustomerRole } = useRoleStore();
  const { setProtectionPlanEnrollment } = useEnrollmentStore();
  const { resettingPassword, cognitoAttributes } = useAuthStore();
  const { setLeadSummaries, setTabs, setNextToken } = useLeadStore();
  const dashboardParam = searchParams.get("dashboard");
  const clientTypeParam = searchParams.get("client_type");
  const clientPortalRedirect = searchParams.get("client-portal-sign-up");
  const clientPortalSignedIn = searchParams.get("client-portal-sign-in");
  const client = new clientConfig();
  let location = useLocation();

  useEffect(() => {
    setMotionLayout({
      layoutLoaded: true
    });
  }, [])

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const number = urlParams.get('t');
    if (number) {
      const cleanNumber = number.replace(/\D/g, '');
      if (!t(`customLoginHeaders.${cleanNumber}.Title`).includes("customLoginHeaders")) {
        setCustomTitle(t(`customLoginHeaders.${cleanNumber}.Title`));
        setCustomBody(t(`customLoginHeaders.${cleanNumber}.Body`));
      }
    }
    const clientType = clientTypeParam?clientTypeParam:'PRO'
    setFeatureFlags({
      dashboardV2: process.env.REACT_APP_DASHBOARD_TWO_ENABLE === 'true' && dashboardParam !== "v1",
      clientType: clientType
    })
    if (resettingPassword) {
      navigate(`/verifycode/${cognitoAttributes?.Email}`, { state: { authenticated: true } });
      Auth.signOut();
    }
    if (clientPortalRedirect != null && clientPortalRedirect === "true") {
      setSuccessMessage("Your password has been successfully created. You can now log in to your account.");
    }
    if (clientPortalSignedIn != null && clientPortalSignedIn === "true") {
      setClientPortalSignIn(true);
    }
    if (location.state?.passwordReset) {
      setSuccessMessage("Your password has been successfully updated! You can now sign in using your new password.");
    }
    if (location.state?.accountCreated) {
      setSuccessMessage("Your email has been verified. Log in using the email address and password you signed up with.");
    }
  }, [location]);

  useEffect(() => {
    validateFormData(email, password);
  }, [email, password]);

  const validateFormData = (_email: string, _password: string) => {
    let _error = error;
    if (!email) {
      error["email"] = t("login.error.email.empty");
    } else if (!emailIsValid(email)) {
      error["email"] = t("login.error.email.invalid");
    } else {
      error["email"] = "";
    }
    if (!password) {
      error["password"] = t("login.error.password.empty");
    } else {
      error["password"] = ""
    }
    setError(_error);
    setError({ ...error });
  };
  const handleInputEmail = (e: any) => {
    setEmail(e.target.value);
  };

  const handleInputPassword = (e: any) => {
    setPassword(e.target.value);
  };

  const togglePassword = () => {
    if (pwdType === "password") {
      setPwdType("text");
      setPwdIcon(faEye);
      return;
    }
    setPwdType("password");
    setPwdIcon(faEyeSlash);
  };

  // const handleRememberMe = (e: any) => {
  //   setRememberMe(!rememberMe);
  // };

  const handleSubmit = async (event: any) => {
    setSubmited(true);
    event.preventDefault();
    event.stopPropagation();
    setShowSpinner(true);

    let isCompleted = false;

    const timeoutFeedback = new Promise<void>(() => {
        setTimeout(() => {
          if (!isCompleted) {
            setFeedbackPrompt(true);
          }
        }, 3000);
      }
    );

    const handleRoleNavigation = async (profileId: string) => {
      const defaultPath = paths.dashboard.taxShield.enrollment;
      const getRoles = await fetchQuery({ ProfileId: profileId }, GetProfileRoles);
      const getSubscription = await fetchQuery({ ProfileId: profileId }, GetProtectionPlanSubscription);

      if (getRoles) {
        setCustomerRole(getRoles.RoleName);
      }

      if (getSubscription) {
        setProtectionPlanEnrollment({
          subscriptionPlan: getSubscription?.ProtectionPlanName,
          subscriptionStatus: getSubscription?.Status,
          enrollmentStatus: '',
        })
      }

      if (getRoles) {
        switch (getRoles.RoleName) {
          case CustomerRoleName.RetailCustomer:
            navigate(paths.dashboard.taxShield.enrollment);
            break;
          case CustomerRoleName.CoClientCustomer:
            navigate("/dashboard");
            break;
          case CustomerRoleName.FreeServiceCustomer:
            navigate(paths.dashboard.taxShield.reports);
            break;
          case CustomerRoleName.TaxPrepReferredCustomer:
            if (getSubscription?.Status && getSubscription.Status.toString() !== SubscriptionStatus.Cancelled) {
              navigate(paths.dashboard.taxShield.reports);
            } else {
              navigate(paths.dashboard.taxShield.enrollment);
            }
            break;
          case CustomerRoleName.NonTaxPrepReferredCustomer:
            if (getSubscription?.Status && getSubscription.Status.toString() !== SubscriptionStatus.Cancelled) {
              navigate(paths.dashboard.taxShield.reports);
            } else {
              navigate(paths.dashboard.taxShield.enrollment);
            }
            break;
          case CustomerRoleName.TaxReliefGraduatedCustomer:
            if (getSubscription?.Status && getSubscription.Status.toString() !== SubscriptionStatus.Cancelled) {
            navigate(paths.dashboard.taxShield.reports);
            }
            else{
              navigate(paths.dashboard.taxShield.enrollment);
            }
            break;
          default:
            navigate(defaultPath);
            break;
        }
      } else {
        navigate(defaultPath);
      }
    }

    Promise.race([
      Auth.signIn(email.toLowerCase(), password),
      timeoutFeedback
    ])
      .then(async (res) => {
        isCompleted = true;
        if (res.attributes["custom:RedirectClientPortal"] === "Yes") {
          setRedirectedToLegacy(true);
          await AuthenticatedToLegacy(email.toLowerCase());
          return;
        } else {
          clearPortalStore();
        }

        if (!res.attributes["custom:FirstSignInTimestamp"]) {
          if (!res.attributes["custom:FirstSignInDateTime"] && res.attributes["custom:LegacyProfileId"]) {
            if (process.env.REACT_APP_LEGACY_PROTECTIONPLAN_ALLOWED === "true") {
              setPrimaryProfile({
                Id: res.attributes["custom:Id"],
                PartnerId: '',
                FirstName: '',
                MiddleName: '',
                LastName: '',
                Phone: '',
                Email: res.attributes["email"],
                LegacyClient: true,
                EnrollmentSource: EnrollmentSource.Legacy
              });
              navigate("/client-profile");
              return;
            } else {
              RedirectToLegacy(res.attributes["email"]);
            }
          }
        }

        if (res.attributes["custom:Id"]) {
          if (res.attributes["custom:LegacyProfileId"]) {
           if (process.env.REACT_APP_LEGACY_PROTECTIONPLAN_ALLOWED === "true") {
             const getProfile = await fetchQuery({ profileId: res.attributes["custom:Id"] }, GetProfile);
             setPrimaryProfile({
               Id: res.attributes["custom:Id"],
               PartnerId: '',
               FirstName: getProfile?.FirstName,
               MiddleName: getProfile?.MiddleName,
               LastName: getProfile?.LastName,
               Phone: getProfile?.Phone,
               Email: res.attributes["email"],
               LegacyClient: true,
               EnrollmentSource: EnrollmentSource.Legacy
             });
             featureFlags?.dashboardV2 ? handleRoleNavigation(res.attributes["custom:Id"]): navigate("/dashboard");
           } else {
            RedirectToLegacy(res.attributes["email"])
            return
           }
          } else {
           if (res.attributes["custom:Group"] === "profile") {
            client
             .query({
              query: GetProfile,
              variables: { profileId: res.attributes["custom:Id"] },
             })
             .then((res: any) => {
              if (res.data.GetProfile !== null) {
                if (res.data.GetProfile?.ParentId) {
                  setSpouseProfile({
                    Id: res.data.GetProfile.Id,
                    AccountId: res.data.GetProfile.AccountId,
                    ParentId: res.data.GetProfile.ParentId,
                    Email: res.data.GetProfile.Email,
                    FirstName: res.data.GetProfile.FirstName,
                    LastName: res.data.GetProfile.LastName,
                  })
                } else {
                  setPrimaryProfile({
                    Id: res.data.GetProfile.Id,
                    PartnerId: res.data.GetProfile.PartnerId,
                    FirstName: res.data.GetProfile.FirstName,
                    MiddleName: res.data.GetProfile.MiddleName ?? "",
                    LastName: res.data.GetProfile.LastName,
                    Phone: res.data.GetProfile.Phone,
                    Email: res.data.GetProfile.Email,
                    Languages: res.data.GetProfile?.Languages ?? null,
                    LegacyClient: false,
                    EnrollmentSource: res.data.GetProfile.PartnerId ? EnrollmentSource.Referral : EnrollmentSource.Organic
                  })
                }
                setEConsent(res.data.GetProfile.EConsent);
                if (featureFlags?.dashboardV2) {
                  handleRoleNavigation(res.data.GetProfile.Id);
                } else {
                  navigate("/dashboard")
                }
              }
             });
           } else {
             client
               .query({
                 query: GetPartner,
                 variables: { PartnerId: res.attributes["custom:Id"] },
               }).then((res: any) => {
                 setPartnerProfile({
                   Id: res.data.GetPartner.Id,
                   Email: res.data.GetPartner.Email,
                   FirstName: res.data.GetPartner.FirstName,
                   LastName: res.data.GetPartner.LastName,
                   Phone: res.data.GetPartner.Phone,
                   ApplicationStatus: res.data.GetPartner.ApplicationStatus,
                   OrganizationId: res.data.GetPartner.OrganizationId
                 });
               const variables = {
                 PartnerId: res.data.GetPartner.Id,
                 Limit: "10",
                 NextToken: 1,
               };
               void fetchQuery(variables, GetLeadsByPartner)
                 .then((getLeadsByPartner: LeadSummaryConnection) => {
                   if (getLeadsByPartner && getLeadsByPartner.LeadSummaries) {
                     setLeadSummaries(getLeadsByPartner.LeadSummaries);
                     const statusArray = getLeadsByPartner.LeadSummaries
                       .map((lead) => lead.LeadStatus)
                       .filter((value, index, array) => array.indexOf(value) === index);
                     setTabs(statusArray);
                     setNextToken(getLeadsByPartner.NextToken || null);
                   }
                 });

                 navigate("/partner-enrollment");
             })
           }
           return;
          }
        }
        if (!res.attributes["custom:LegacyProfileId"]) {
          if (res.attributes["custom:Group"] === "profile") {
            setPrimaryProfile({
              Id: '',
              PartnerId: '',
              Email: res.attributes?.email,
              FirstName: '',
              MiddleName: '',
              LastName: '',
              Phone: '',
              LegacyClient: false,
              EnrollmentSource: null
            });
            navigate("/client-profile");
          } else {
            navigate("/partner-profile");
          }
        }
      })
      .catch((error) => {
        isCompleted = true;
        setShowSpinner(false);
        setFeedbackPrompt(false);
        const code = error.code;
        console.log(error.message);
        switch (code) {
          case "NotAuthorizedException":
            let _error1 = error;
            _error1["password"] = error.message;
            setError(_error1);
            setError({ ...error });
            break;
          case "UserNotConfirmedException":
            return UserNotConfirmedRedirection();
          case "UserNotFoundException":
           if (error.message === 'UserMigration failed with error Legacy Protection Plan Client (Portal User).') {
            setShowPortalModal(true);
            break;
           } else {
            let _error2 = error;
            if (error.message === "UserMigration failed with error Invalid Password.") {
             _error2["password"] = "Incorrect username or password.";
            } else {
             _error2["email"] = "User does not exist";
            }
            setError(_error2);
            setError({ ...error });
           }
            break;
          case "PasswordResetRequiredException":
            Auth.forgotPassword(email.toLowerCase()).then((res) => {
              navigate(
                "/resetPassword/" + Buffer.from(email).toString("base64")
              );
            });
            break;
         case "ResourceNotFoundException":
          if (error.message === "Device does not exist.") {
           setShowSpinner(true);
           localStorage.clear();
          }
           Auth.signIn(email.toLowerCase(), password).then((res) => {
            if (!res.attributes["custom:FirstSignInTimestamp"]) {
              if (!res.attributes["custom:FirstSignInDateTime"] && res.attributes["custom:LegacyProfileId"]) {
                if (process.env.REACT_APP_LEGACY_PROTECTIONPLAN_ALLOWED === "true") {
                  navigate("/client-profile");
                } else {
                  RedirectToLegacy(res.attributes["email"]);
                }
              } else {
                featureFlags?.dashboardV2 ? navigate(paths.dashboard.taxShield.enrollment) : navigate("/dashboard");
              }
            }
           });
           break;
          default:
            let _error3 = error;
            _error3["password"] = error.message;
            setError(_error3);
            setError({ ...error });
            break;
        }
      });
  };

  const UserNotConfirmedRedirection = () => {
    setShowSpinner(true);
    Auth.signOut().then((res) => {
      Auth.resendSignUp(email.toLowerCase())
        .then((res) => {
          navigate(`/verifycode/${email}`);
        })
        .catch((error) => {
          let code = error.code;
          if (code === "LimitExceededException") {
            setDisableSubmit(true);
            setLimitExceeded(true);
          }
          console.log(error);
          setShowSpinner(false);
        });
    });
  };

  const motionContainer = {
    visible: {
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const motionItem = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1
    }
  };

  const dotVariants = {
    animate: (i: number) => ({
      color: ['#4c5360', '#0d4d90', '#4c5360'],
      y: [0, -5, 0],
      transition: {
        duration: 1.5,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: i * 0.5,
      },
    }),
  };

  return (
    <DefaultLayout>
      <PageBody className="page-body">
        <Snackbar open={successMessage !== null} onClose={() => setSuccessMessage(null)}
                  anchorOrigin={ {vertical: 'top', horizontal: 'center' }}>
          <Alert
            onClose={() => setSuccessMessage(null)}
            severity="success"
            variant="filled"
            sx={{ width: '100%', backgroundColor: "#318335", color: "#fff" }}>
            {successMessage}
          </Alert>
        </Snackbar>
       {showPortalModal && <PortalClientModal showPortalModal={showPortalModal} setShowPortalModal={setShowPortalModal} />}
        <form onSubmit={handleSubmit}>
          <motion.div variants={motionContainer} initial="hidden" animate="visible">
            <motion.div variants={motionItem} style={{listStyleType: 'none'}}>
            <FormTitle className="tab-signin-title">
              {customTitle ? customTitle : reset !== "reset" ? t("login.title") : t("login.resetTitle")}
            </FormTitle>
            <p className="content-text mb-4">{customBody ? customBody : t("login.enterDetail")}</p>
            </motion.div>
            <FormGroup className="mb-3" id="formBasicEmail">
              <motion.div variants={motionItem} style={{listStyleType: 'none'}}>
              <FormLabel>{t("login.email")}</FormLabel>
              <Form.Control
                required
                type="email"
                value={email}
                onChange={handleInputEmail}
                placeholder="Enter email"
                className={
                  "input-no-bg " + (submited && error.email ? "is-invalid" : "")
                }
              />
              {submited && error.email && (
                <Message message={error.email} type="error"></Message>
              )}
              </motion.div>
            </FormGroup>
            <motion.div variants={motionItem}>
            <FormGroup className="mb-3" id="password">
              <FormLabel>{t("login.password")}</FormLabel>
              <div className="position-relative">
                <Form.Control
                  type={pwdType}
                  value={password}
                  placeholder="Password"
                  required
                  onChange={handleInputPassword}
                  className={
                    "input-no-bg " +
                    (submited && error.password ? "is-invalid" : "")
                  }
                />
                <FontAwesomeIcon
                  onClick={togglePassword}
                  className="addOn"
                  icon={pwdIcon}
                />
              </div>
              {submited && error.password && (
                <Message message={error.password} type="error"></Message>
              )}
            </FormGroup>
            <Row>
              {/* <Col>
                <Form.Group className="mb-3 justify-item-center d-block">
                  <Form.Check
                    type="checkbox"
                    label={t("login.rememberme")}
                    onChange={handleRememberMe}
                    id="rememberme"
                  />
                </Form.Group>
              </Col> */}
              <Col>
                <TextButton
                  className="float-end mb-3 text-decoration-underline"
                  style={{color: "#1589d2"}}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/forgetpassword");
                  }}
                >
                  {t("login.btn.forgotPassword")}
                </TextButton>
              </Col>
            </Row>
          </motion.div>
              <AnimatePresence>
                {feedbackPrompt && (
                  <motion.div variants={motionItem}>
                    <motion.p
                      key={"feedbackPrompt"}
                      className="content-text mb-4 center"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ ease: "easeInOut", duration: 0.3 }}
                    >
                      Almost there! We're just confirming your credentials
                      <span style={{ whiteSpace: 'nowrap' }}>
                        &nbsp;
                        <AnimatedDot delay="0s">.</AnimatedDot>
                        <AnimatedDot delay="0.5s">.</AnimatedDot>
                        <AnimatedDot delay="1s">.</AnimatedDot>
                    </span>
                    </motion.p>
                  </motion.div>
                )}
              </AnimatePresence>
            <motion.div variants={motionItem}>
              {limitExceed &&
                <Message message={"Attempt limit exceeded, please try again after 15 minutes."} type="error"></Message>
              }
              <Button type="submit" disabled={showSpinner || limitExceed}
              className="w-100" variant="primary">
              {showSpinner &&
              <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="mr-4"
              />
              {'  '}
              </>
              }
              {t("login.btn.signin")}
            </Button>
            <TermSentence>
              {t("login.sentence.part1")}
              <ForgotPasswordLink
                href="https://optimataxrelief.com/about-us/terms-of-site/"
                target="_blank"
                rel="noreferrer"
                className="text-reset text-decoration-underline"
              >
                {t("login.terms")}{" "}
              </ForgotPasswordLink>
              {t("login.sentence.part2")}
              <ForgotPasswordLink
                href="https://optimataxrelief.com/about-us/privacy/"
                target="_blank"
                rel="noreferrer"
                className="text-reset text-decoration-underline"
              >
                {t("login.privacy")}{" "}
              </ForgotPasswordLink>
            </TermSentence>
              {!clientPortalSignIn  &&
                <NotAccount>
                {t("login.dontHaveAccount")}
                <TextButton
                  style={{ color: "#1589d2" }}
                  onClick={(e) => {
                    navigate("/signup");
                  }}
                >
                  {t("login.btn.signup")}{" "}
                </TextButton>
              </NotAccount>}
            </motion.div>
          </motion.div>
        </form>
      </PageBody>
    </DefaultLayout>
  );
};

export default LoginPage;

const PageBody = styled.div`
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding: 5rem 2rem;
`;

const FormTitle = styled.span`
  font-size: 1.8rem;
  font-weight: 700;
  color: #0d4d90;
  font-family: 'Montserrat', sans-serif;
`;

const FormGroup = styled.div``;
const FormLabel = styled.label`
  font-size: 0.8rem;
  margin-bottom: 0.4rem;
`;
const ForgotPasswordLink = styled.a`
  text-decoration: underline;
  cursor: pointer;
`;

const TextButton = styled.span`
  color: #0d4d90;
  text-align: center;
  cursor: pointer;
  font-weight: 700;
`;

const TermSentence = styled.div`
  align-items: center;
  text-align: center;
  margin-top: 1rem;
  font-style: italic;
`;

const NotAccount = styled.div`
  align-items: center;
  text-align: center;
  margin-top: 1rem;
`;

const dotAnimation = keyframes`
  0% {
    transform: translateY(0);
  }
  33% {
    transform: translateY(-5px);
  }
  66% {
    transform: translateY(0);
  }
  100% {
    transform: translateY(0);
  }
`;

const AnimatedDot = styled.span<AnimatedDotProps>`
  display: inline-block;
  margin-right: 2px;
  font-weight: bold;
  font-size: 14px;
  animation: ${dotAnimation} 1.5s infinite ease-in-out;
  animation-delay: ${(props) => props.delay || '0s'};
`;
